import React from 'react';
import { FileText, MessageSquare, Mic } from 'lucide-react';

interface PreferencesStepProps {
  data: any;
  onUpdate: (updates: any) => void;
  onNext: () => void;
}

export default function PreferencesStep({ data, onUpdate, onNext }: PreferencesStepProps) {
  const additionalPreferences = data.additionalPreferences || '';
  const communicationPreference = data.communicationPreference || 'text-and-voice';

  const handlePreferencesChange = (value: string) => {
    onUpdate({ additionalPreferences: value });
  };

  const handleCommunicationChange = (value: string) => {
    onUpdate({ communicationPreference: value });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <FileText className="h-12 w-12 text-pink-400 hidden md:block mx-auto mb-4" />
        <h3 className="md:text-lg font-semibold text-gray-900 mb-2">
          Additional Preferences
        </h3>
        <p className="md:text-base text-sm text-gray-600">
          Tell us more about how you'd like your coach to work with you
        </p>
      </div>

      <div className="space-y-6">
        {/* Communication Preferences */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            How would you like to communicate?
          </label>
          <div className="space-y-3">
            <label className="flex items-center space-x-3 p-4 border border-gray-200 rounded-xl hover:border-pink-300 transition-colors cursor-pointer">
              <input
                type="radio"
                name="communicationPreference"
                value="text-only"
                checked={communicationPreference === 'text-only'}
                onChange={(e) => handleCommunicationChange(e.target.value)}
                className="w-4 h-4 text-pink-400 border-gray-300 focus:ring-pink-400"
              />
              <MessageSquare className="h-5 w-5 text-gray-600" />
              <div>
                <span className="font-medium text-gray-900">Text only</span>
                <p className="text-sm text-gray-600">Simple text messages only</p>
              </div>
            </label>
            
            <label className="flex items-center space-x-3 p-4 border border-gray-200 rounded-xl hover:border-pink-300 transition-colors cursor-pointer">
              <input
                type="radio"
                name="communicationPreference"
                value="text-and-voice"
                checked={communicationPreference === 'text-and-voice'}
                onChange={(e) => handleCommunicationChange(e.target.value)}
                className="w-4 h-4 text-pink-400 border-gray-300 focus:ring-pink-400"
              />
              <div className="flex items-center space-x-2">
                <MessageSquare className="h-5 w-5 text-gray-600" />
                <Mic className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <span className="font-medium text-gray-900">Text and voice</span>
                <p className="text-sm text-gray-600">Both text messages and voice notes</p>
              </div>
            </label>
          </div>
        </div>

        {/* Additional Preferences Text Area */}
        <div>
          <label htmlFor="preferences" className="block text-sm font-medium text-gray-700 mb-2">
            Any specific preferences or instructions for your AI coach? (Optional)
          </label>
          <textarea
            id="preferences"
            value={additionalPreferences}
            onChange={(e) => handlePreferencesChange(e.target.value)}
            placeholder="For example: 'I work best with visual reminders', 'I prefer morning motivation over evening pressure', 'I have ADHD and need extra patience', etc."
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-transparent resize-none"
            rows={6}
            maxLength={500}
          />
          <div className="flex justify-between items-center mt-2">
            <p className="text-sm text-gray-500">
              This helps personalize your coaching experience
            </p>
            <span className={`text-sm ${additionalPreferences.length > 450 ? 'text-amber-600' : 'text-gray-500'}`}>
              {additionalPreferences.length}/500
            </span>
          </div>
        </div>

        {/* Quick suggestions */}
        <div className="bg-gray-50 rounded-xl p-4">
          <h4 className="font-medium text-gray-900 mb-3">Quick suggestions:</h4>
          <div className="grid grid-cols-2 gap-2">
            {[
              'I work best with...',
              'I struggle most with...',
              'My biggest motivation is...',
              'Please avoid...',
              'I have [condition] that affects...',
              'I prefer [time/style]...'
            ].map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => {
                  if (!additionalPreferences.includes(suggestion)) {
                    const newText = additionalPreferences ? `${additionalPreferences}\n\n${suggestion}` : suggestion;
                    if (newText.length <= 500) {
                      handlePreferencesChange(newText);
                    }
                  }
                }}
                className="px-3 py-2 text-xs bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-pink-400 text-left border border-gray-200"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}